import clsx from "clsx";
import { ReactNode } from "react";

import { TruncatedText } from "@/components/Interface/TruncatedText/TruncatedText";
import { useSectionHeadingLevel } from "@/components/Layout/BaseLayouts/SectionHeadingLevel";

const headingTag = { 2: "h2", 3: "h3" } as const;

interface ProductCardHeaderProps {
  icon: ReactNode;
  title: string;
  /** Heading level of the title. Defaults to the surrounding section's level. */
  headingLevel?: 2 | 3;
  subtitle?: string;
  /** e.g. "Being paused on 10.07.2026" — a short status/date line below the subtitle */
  note?: ReactNode;
  /** e.g. one or more <ProductCardChip /> */
  chips?: ReactNode;
  /** Top-right action, e.g. a "Manage" button */
  action?: ReactNode;
  className?: string;
}

export const ProductCardHeader = ({
  icon,
  title,
  headingLevel,
  subtitle,
  note,
  chips,
  action,
  className,
}: ProductCardHeaderProps) => {
  const sectionHeadingLevel = useSectionHeadingLevel();

  return (
    <div
      className={clsx(
        "relative flex h-full w-full flex-col items-start gap-3 pt-4",
        className,
      )}
    >
      {action && <div className="absolute right-0 top-0">{action}</div>}
      <div className="size-14 shrink-0">{icon}</div>
      <div className="flex w-full flex-col gap-1 text-foreground-primary">
        <TruncatedText
          as={headingTag[headingLevel ?? sectionHeadingLevel]}
          className="w-full text-headline-sm font-semibold leading-md"
          lines={2}
          text={title}
        />
        {subtitle && (
          <p className="truncate text-body-lg font-semibold leading-md">
            {subtitle}
          </p>
        )}
        {note && <p className="text-body-xs leading-none">{note}</p>}
      </div>
      {chips && (
        <div className="flex w-full flex-wrap items-start gap-2">{chips}</div>
      )}
    </div>
  );
};
